/**
 * Начисления и оплата — ТОЧНАЯ КОПИЯ legacy htdocs/core/templates/main_payment.tpl
 * (разметка + весь inline-JS шаблона, переложенный в React-обработчики).
 *
 * Соответствие legacy:
 *   .curareaid                     — скрытый div с data-areaid (id участка)
 *   .modalblock                    — контейнер модалки подтверждения оплаты
 *   .balanceblockheader/.balanceblock — «Баланс:» + «№ л/с:»
 *   .balanceblockbody              — сюда legacy вставлял iframe формы банка (iFrameResize minHeight 900)
 *   .debtblockheader/.debtblock    — «Задолженность:» (при нуле — серым и «Нет»)
 *   .invoicessheadblock + table.invosesstable — выставленные квитанции (6 колонок)
 *   .historyheadblock  + table.invosesstable  — история по счёту (4 колонки)
 *   .invoicessaddbottom/.historyaddbottom     — стрелка «показать ещё» (+10 строк)
 *
 * Легаси-эндпоинты → API реворка:
 *   /invoceslist          → GET  /api/invoices
 *   /paymentshistorylist  → GET  /api/invoices/history
 *   /getbalance,/getdebtsum,/checksetautoinvoicespay → GET /api/invoices/summary
 *   /afterpayment         → GET  /api/invoices/{id}/after-payment
 *   /invoicepay           → POST /api/invoices/{id}/pay
 *   /setautoinvoicespay   → POST /api/invoices/autopay
 *   /payment_create       → POST /payment_create (легаси-путь, ответ text/plain)
 *   /getpdfinvoice        → без изменений
 *
 * ВАЖНО (1:1 с legacy): блоки пополнения (.addpaymentblock) и автооплаты
 * (.autopaymantparam) в main_payment.tpl ЗАКОММЕНТИРОВАНЫ — на живом сайте они не
 * отображаются (эквайринг не используется). Разметка и поведение перенесены полностью
 * и включаются флагом LEGACY_TOPUP_UI: при false страница отрисовывается ровно так же,
 * как legacy сегодня.
 */

import { useEffect, useMemo, useState } from 'react'

import { api } from '@/api/client'
import type { Invoice, Transaction } from '@/api/types'
import { isTrue, toNumber } from '@/lib/format'
import { useApi } from '@/lib/useApi'
import { useSession } from '@/session/SessionProvider'

/** legacy main_payment.tpl: var invoicetype = {...} — подписи именно в нижнем регистре. */
const INVOICE_TYPE: Record<string, string> = {
  electro: 'электроэнергия',
  chlenskie: 'членский взнос',
  celevie: 'целевой взнос',
  other: 'прочее',
}

/**
 * Блоки «Пополнить» и «Автооплата квитанций…» закомментированы в legacy-шаблоне.
 * Ставим true — и возвращается вся legacy-разметка вместе с поведением
 * (payment_create → iframe формы банка с iFrameResize minHeight 900).
 */
const LEGACY_TOPUP_UI = false

const IFRAME_RESIZER_SRC = 'https://secure.openbank.ru/openapi/deploy/open_payments/js/iframeResizer.min.js'

declare global {
  interface Window {
    iFrameResize?: (
      options: { log: boolean; checkOrigin: boolean; minHeight: number },
      target: string,
    ) => void
  }
}

/** GET /api/invoices/summary (legacy: getbalance + getdebtsum + checksetautoinvoicespay). */
interface Summary {
  balance: string
  debt: string
  unpaid_count: number
  autopay: boolean
}

/** Данные модалки .alertPaymentModal (legacy alertPaymentModal(invid, type, date, sum)). */
interface PaymentModalData {
  invid: number
  type: string
  date: string
  sum: string
  /** Ответ /afterpayment: баланс после оплаты, формат «1 234,56». */
  afterpayment: string
}

/** legacy date_normalize_with_time(): «2024-03-15 12:00:00» → «15.03.2024 12:00:00». */
function dateNormalizeWithTime(value: string | null): string {
  if (value === null || value === '') return ''
  const date = new Date(value.replace(' ', 'T'))
  const pad = (n: number): string => (n < 10 ? '0' + String(n) : String(n))
  return (
    pad(date.getDate()) +
    '.' +
    pad(date.getMonth() + 1) +
    '.' +
    String(date.getFullYear()) +
    ' ' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds())
  )
}

/** legacy date_normalize(): «2024-03-15 12:00:00» → «15.03.2024 » (с хвостовым пробелом). */
function dateNormalize(value: string | null): string {
  if (value === null || value === '') return ''
  const date = new Date(value.replace(' ', 'T'))
  const pad = (n: number): string => (n < 10 ? '0' + String(n) : String(n))
  return pad(date.getDate()) + '.' + pad(date.getMonth() + 1) + '.' + String(date.getFullYear()) + ' '
}

/** legacy: electro — сырой date_end, остальные — date_normalize(invoce_date_add). */
function invoiceDate(invoice: Invoice): string {
  return invoice.typeinvoice === 'electro'
    ? (invoice.date_end ?? '')
    : dateNormalize(invoice.invoce_date_add)
}

export function PaymentPage() {
  const { user } = useSession()

  const summary = useApi<Summary>('/api/invoices/summary')
  const invoicesRequest = useApi<Invoice[]>('/api/invoices')
  const historyRequest = useApi<Transaction[]>('/api/invoices/history')

  const invoiceslist = useMemo(() => invoicesRequest.data ?? [], [invoicesRequest.data])
  const paymentslist = useMemo(() => historyRequest.data ?? [], [historyRequest.data])

  // legacy: #lastteninvoice (checked+disabled), #unpaidinvoicess, invoiceslistcount = 3
  const [lastTenInvoice, setLastTenInvoice] = useState(true)
  const [lastTenInvoiceDisabled, setLastTenInvoiceDisabled] = useState(true)
  const [unpaidInvoices, setUnpaidInvoices] = useState(false)
  const [invoicesCount, setInvoicesCount] = useState(3)

  // legacy: #lasttentransaction (checked+disabled), paymentslistcount = 3
  const [lastTenTransaction, setLastTenTransaction] = useState(true)
  const [lastTenTransactionDisabled, setLastTenTransactionDisabled] = useState(true)
  const [paymentsCount, setPaymentsCount] = useState(3)

  const [modal, setModal] = useState<PaymentModalData | null>(null)

  // .addpaymentblock / .balanceblockbody (в legacy закомментированы, см. LEGACY_TOPUP_UI)
  const [amountPayment, setAmountPayment] = useState('')
  const [bankFormUrl, setBankFormUrl] = useState<string | null>(null)
  const [bankFormLoading, setBankFormLoading] = useState(false)
  const [autopay, setAutopay] = useState(false)

  const balance = summary.data?.balance ?? null
  const debt = summary.data?.debt ?? null
  // legacy getDebtsum(): parseFloat(data.replace(' ', '')) == 0.0 → «Нет» серым цветом
  const noDebt = debt !== null && toNumber(debt) === 0

  // legacy getDebtsum(): при долге подставляет его в поле суммы пополнения
  useEffect(() => {
    if (debt !== null && toNumber(debt) !== 0) setAmountPayment(debt.replace(' ', ''))
  }, [debt])

  // legacy: чекбокс автооплаты инициализируется из t_area.invoiceautopay
  useEffect(() => {
    if (summary.data !== null) setAutopay(summary.data.autopay)
  }, [summary.data])

  // legacy: iFrameResize({log:false, checkOrigin:false, minHeight:900}, "#openPaymentsFrame")
  useEffect(() => {
    if (bankFormUrl === null) return
    let cancelled = false

    void loadIframeResizer().then(() => {
      if (cancelled) return
      window.iFrameResize?.({ log: false, checkOrigin: false, minHeight: 900 }, '#openPaymentsFrame')
    })

    return () => {
      cancelled = true
    }
  }, [bankFormUrl])

  function reloadAll(): void {
    summary.reload()
    invoicesRequest.reload()
    historyRequest.reload()
  }

  /** legacy: $('#lastteninvoice, #unpaidinvoicess').on('click') — ветка #unpaidinvoicess. */
  function onUnpaidInvoicesClick(checked: boolean): void {
    setUnpaidInvoices(checked)
    if (!lastTenInvoice) {
      setLastTenInvoice(true)
      setLastTenInvoiceDisabled(true)
    } else {
      setLastTenInvoice(false)
      setLastTenInvoiceDisabled(false)
    }
    setInvoicesCount(3)
  }

  /** legacy: та же связка — ветка #lastteninvoice. */
  function onLastTenInvoiceClick(checked: boolean): void {
    setLastTenInvoice(checked)
    if (unpaidInvoices) {
      setUnpaidInvoices(false)
      setLastTenInvoiceDisabled(true)
    }
    setInvoicesCount(3)
  }

  /** legacy: $('body').on('click', '.invoicessaddbottom') — +10 строк. */
  function onInvoicesAddBottom(): void {
    setLastTenInvoice(false)
    setLastTenInvoiceDisabled(false)
    setInvoicesCount((count) => count + 10)
  }

  /** legacy: $('#lasttentransaction').on('click'). */
  function onLastTenTransactionClick(checked: boolean): void {
    setLastTenTransaction(checked)
    setLastTenTransactionDisabled(true)
    setPaymentsCount(3)
  }

  /** legacy: $('body').on('click', '.historyaddbottom') — +10 строк. */
  function onHistoryAddBottom(): void {
    setLastTenTransactionDisabled(false)
    setLastTenTransaction(false)
    setPaymentsCount((count) => count + 10)
  }

  /** legacy: клик по .paycurinvoce → alertPaymentModal(): сначала /afterpayment, потом модалка. */
  async function openPaymentModal(invoice: Invoice): Promise<void> {
    const afterpayment = await api.get<string>(`/api/invoices/${invoice.smsid}/after-payment`)
    setModal({
      invid: invoice.smsid,
      type: invoice.typeinvoice,
      date: invoiceDate(invoice),
      sum: invoice.totalcost,
      afterpayment,
    })
  }

  /** legacy: .accept_payment → /invoicepay, при success_payed строка становится «Оплачено». */
  async function acceptPayment(): Promise<void> {
    if (modal === null) return
    const result = await api.post<{ status: string }>(`/api/invoices/${modal.invid}/pay`, {
      type: modal.type,
    })
    if (result.status === 'success_payed') {
      setModal(null)
      reloadAll()
    }
  }

  /** legacy: .amount_payment / .amount_payment_modal — фильтр ввода /^[0-9,.]{0,9}$/. */
  function onAmountInput(value: string): void {
    if (/^[0-9,.]{0,9}$/.test(value)) setAmountPayment(value)
  }

  /**
   * legacy: .addpaymentbtn → POST /payment_create, ответ — URL формы банка,
   * который вставляется в iframe внутри .balanceblockbody.
   * Ответ text/plain (Response::raw), поэтому JSON-конверт api-клиента здесь неприменим.
   */
  async function addPayment(): Promise<void> {
    if (toNumber(amountPayment) < 1) return

    setBankFormUrl(null)
    setBankFormLoading(true)

    const body = new FormData()
    body.set('new_payment', 'new_payment')
    body.set('amount', amountPayment)

    const response = await fetch('/payment_create', { method: 'POST', body, credentials: 'same-origin' })
    const url = (await response.text()).trim()

    setBankFormLoading(false)
    setBankFormUrl(url)
  }

  /** legacy: .autoinvoicespay change → confirm(), если есть неоплаченные квитанции. */
  async function onAutopayChange(checked: boolean): Promise<void> {
    const unpaidCount = summary.data?.unpaid_count ?? 0
    if (checked && unpaidCount > 0) {
      const agreed = window.confirm(
        'Имеются не оплаченые квитанции, желаете активировать автооплату? Все квитанции будут оплачены автоматически при наличии средств на счету',
      )
      if (!agreed) {
        setAutopay(false)
        return
      }
    }
    setAutopay(checked)
    await api.post('/api/invoices/autopay', { value: String(checked) })
    reloadAll()
  }

  // legacy initinvoices(): фильтр «неоплаченные» + ограничение invoiceslistcount
  const visibleInvoices: Invoice[] = []
  for (const invoice of invoiceslist) {
    if (unpaidInvoices && (isTrue(invoice.waspaid) || isTrue(invoice.oldinv))) continue
    visibleInvoices.push(invoice)
    if (visibleInvoices.length === invoicesCount && (!unpaidInvoices || lastTenInvoice)) break
  }
  const invoicesAddBottom =
    invoiceslist.length !== invoicesCount &&
    invoiceslist.length > invoicesCount &&
    (!unpaidInvoices || lastTenInvoice)

  // legacy initpayments(): ограничение paymentslistcount
  const visiblePayments: Transaction[] = []
  for (const transaction of paymentslist) {
    visiblePayments.push(transaction)
    if (visiblePayments.length === paymentsCount && lastTenTransaction) break
  }
  const historyAddBottom =
    paymentslist.length !== paymentsCount && paymentslist.length > paymentsCount && lastTenTransaction

  // legacy alertPaymentModal(): needpayment = afterpayment < 0
  const needPayment =
    modal !== null &&
    Number.parseFloat(modal.afterpayment.replace(' ', '').replace(',', '.')) < 0

  return (
    <>
      <div className="curareaid" data-areaid={user?.id} />

      <div className="modalblock">
        {modal !== null && (
          <div className="alertPaymentModal">
            <div className="modalheader">
              <div className="headertitle">Подтверждение оплаты</div>
              <div className="headerclose" onClick={() => setModal(null)}>
                X
              </div>
            </div>
            <div className="modalbody">
              <table>
                <tbody>
                  <tr>
                    <td>Тип:</td>
                    <td className="pl10">{INVOICE_TYPE[modal.type]}</td>
                  </tr>
                  <tr>
                    <td>Дата:</td>
                    <td className="pl10">{modal.date}</td>
                  </tr>
                  <tr>
                    <td>Сумма:</td>
                    <td className="pl10">{modal.sum} руб.</td>
                  </tr>
                  {!needPayment && (
                    <tr>
                      <td>Баланс после оплаты:</td>
                      <td className="pl10">{modal.afterpayment} руб.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {needPayment && (
                <p
                  style={{
                    paddingTop: '30px',
                    color: '#c40000',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  На счете недостаточно средств для оплаты
                </p>
              )}
            </div>
            <div className="modalfooter">
              {!needPayment && (
                <button className="accept_payment" onClick={() => void acceptPayment()}>
                  Подтвердить оплату
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="ptop" />

      {/* legacy: блок автооплаты в main_payment.tpl закомментирован */}
      {LEGACY_TOPUP_UI && (
        <div className="row">
          <div className="col-md-10 col-md-offset-1">
            <div className="autopaymantparam">
              <div className="checkboxswitch">
                <label className="switch">
                  <input
                    type="checkbox"
                    className="autoinvoicespay"
                    checked={autopay}
                    onChange={(event) => void onAutopayChange(event.target.checked)}
                  />
                  <span className="slider" />
                </label>
              </div>
              <div className="title">Автооплата квитанций при пополнении баланса</div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className="balanceblockheader row">
            <div className="balanceblock col-sm-7 col-xs-12">
              <div className="balancetext">Баланс:</div>
              <div className="balancecount">{balance !== null && `${balance} руб.`}</div>
              <div className="pidtext">№ л/с:</div>
              <div className="pidcount">{user?.area_pid}</div>
            </div>

            {/* legacy: блок пополнения в main_payment.tpl закомментирован */}
            {LEGACY_TOPUP_UI && (
              <div className="addpaymentblock col-sm-5 col-xs-12">
                <input
                  type="text"
                  className="amount_payment"
                  placeholder="сумма в рублях"
                  value={amountPayment}
                  onChange={(event) => onAmountInput(event.target.value)}
                />
                <button className="addpaymentbtn lkpaymentbtn" onClick={() => void addPayment()}>
                  Пополнить
                </button>
              </div>
            )}
          </div>

          <div className="balanceblockbody">
            {bankFormLoading && <p style={{ textAlign: 'center' }}>Загрузка данных по платежу...</p>}
            {bankFormUrl !== null && (
              <>
                <div className="balanceblockbodyclose" onClick={() => setBankFormUrl(null)}>
                  <i className="fa fa-angle-double-up" aria-hidden="true" />
                </div>
                <style>{'iframe{width: 1px;min-width: 100%;border: none;}'}</style>
                <iframe id="openPaymentsFrame" src={bankFormUrl} scrolling="no" />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className="debtblockheader row">
            <div className="debtblock col-sm-7 col-xs-12">
              <div className="debttext" style={noDebt ? { color: 'grey' } : undefined}>
                Задолженность:
              </div>
              <div className="debtcount" style={noDebt ? { color: 'grey' } : undefined}>
                {debt !== null && (noDebt ? 'Нет' : `${debt} руб.`)}
              </div>

              {/* legacy: кнопка «Оплатить все» в main_payment.tpl закомментирована */}
              {LEGACY_TOPUP_UI && !noDebt && (
                <button className="addpaymentbtn lkpaymentallbtn" onClick={() => void addPayment()}>
                  Оплатить все
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className="invoicessheadblock">
            <div className="hhtitle">Выставленные квитанции:</div>
            <div className="hhlasttransactions">
              <label htmlFor="lastteninvoice" className="labellastteninvoice">
                <input
                  type="checkbox"
                  name="lastteninvoice"
                  id="lastteninvoice"
                  checked={lastTenInvoice}
                  disabled={lastTenInvoiceDisabled}
                  onChange={(event) => onLastTenInvoiceClick(event.target.checked)}
                />
                последние
              </label>
            </div>
            <div className="hhlasttransactions">
              <label htmlFor="unpaidinvoicess" className="labelunpaidinvoicess">
                <input
                  type="checkbox"
                  name="unpaidinvoicess"
                  id="unpaidinvoicess"
                  checked={unpaidInvoices}
                  onChange={(event) => onUnpaidInvoicesClick(event.target.checked)}
                />
                неоплаченные
              </label>
            </div>
          </div>
          <div className="invoicessbodyblock">
            <table className="invosesstable">
              <thead>
                <tr className="bodylistinvoicessitem">
                  <th className="invoicessdata">Дата</th>
                  <th className="invoicesssumm">Участок</th>
                  <th className="invoicesssumm">Сумма</th>
                  <th className="invoicesskvit">Квитанция</th>
                  <th className="invoicesstype">Тип</th>
                  <th className="invoicessstatus">Статус</th>
                </tr>
              </thead>
              <tbody className="bodylistinvocesitembl">
                {visibleInvoices.length === 0 && (
                  <tr className="bodylistinvoicessitem">
                    <td colSpan={6} align="center">
                      Неоплаченные квитанции отсутствуют.
                    </td>
                  </tr>
                )}
                {visibleInvoices.map((invoice) => (
                  <tr className="bodylistinvoicessitem" key={invoice.smsid}>
                    <td className="invoicessdata">{invoiceDate(invoice)}</td>
                    <td className="invoicessdata">{invoice.areaname !== null ? `№${invoice.areaname}` : ''}</td>
                    <td className="invoicesssumm">{invoice.totalcost} руб.</td>
                    <td className="invoicesskvit">
                      <a
                        href={`/getpdfinvoice?invid=${invoice.smsid}&type=${invoice.typeinvoice}`}
                        target="_blank"
                        rel="noreferrer"
                        // Квитанция закрыта авторизацией — качаем с заголовком и открываем
                        // blob в новой вкладке (по ссылке браузер токен не пошлёт).
                        onClick={(event) => {
                          event.preventDefault()
                          void api.downloadFile(
                            `/getpdfinvoice?invid=${invoice.smsid}&type=${invoice.typeinvoice}`,
                            `Квитанция ${invoice.smsid}.pdf`,
                            true,
                          )
                        }}
                      >
                        <i className="far fa-file-pdf" />
                      </a>
                    </td>
                    <td className="invoicesstype">{INVOICE_TYPE[invoice.typeinvoice]}</td>
                    <td className="invoicesstatus">
                      {isTrue(invoice.oldinv)
                        ? ''
                        : isTrue(invoice.waspaid)
                          ? 'Оплачено'
                          : (
                              <button
                                className="paycurinvoce lkpaymentinv"
                                onClick={() => void openPaymentModal(invoice)}
                              >
                                Оплатить
                              </button>
                            )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="footlistinvocesitembl">
                {invoicesAddBottom && (
                  <tr>
                    <td colSpan={6} align="center">
                      <div className="invoicessaddbottom" onClick={onInvoicesAddBottom}>
                        <i className="fa fa-angle-double-down" aria-hidden="true" />
                      </div>
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          <div className="invoicesslistaddarrow" />
        </div>
      </div>

      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className="historyheadblock">
            <div className="hhtitle">История по счету:</div>
            <div className="hhlasttransactions">
              <label htmlFor="lasttentransaction" className="labellasttentransaction">
                <input
                  type="checkbox"
                  name="lasttentransaction"
                  id="lasttentransaction"
                  checked={lastTenTransaction}
                  disabled={lastTenTransactionDisabled}
                  onChange={(event) => onLastTenTransactionClick(event.target.checked)}
                />
                последние
              </label>
            </div>
          </div>
          <div className="historybodyblock">
            <table className="invosesstable">
              <thead>
                <tr className="bodylisthistoryitem">
                  <th className="historydata">Дата</th>
                  <th className="historydata">Участок</th>
                  <th className="historysumm">Сумма</th>
                  <th className="historytype">Тип</th>
                </tr>
              </thead>
              <tbody className="bodylisthistoryitembl">
                {visiblePayments.length === 0 && (
                  <tr className="bodylisthistoryitem">
                    <td colSpan={4} align="center">
                      Нет транзакций по счету.
                    </td>
                  </tr>
                )}
                {visiblePayments.map((transaction) => (
                  <tr className="bodylisthistoryitem" key={transaction.id}>
                    <td className="historydata">{dateNormalizeWithTime(transaction.time)}</td>
                    <td className="historydata">
                      {transaction.areaname !== null ? `№${transaction.areaname}` : ''}
                    </td>
                    <td className="historysumm">{transaction.sum} руб.</td>
                    <td className="historytype">{transaction.name}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="footlisthistoryitembl">
                {historyAddBottom && (
                  <tr>
                    <td colSpan={4} align="center">
                      <div className="historyaddbottom" onClick={onHistoryAddBottom}>
                        <i className="fa fa-angle-double-down" aria-hidden="true" />
                      </div>
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          <div className="historylistaddarrow" />
        </div>
      </div>
    </>
  )
}

/** legacy подключал iframeResizer.min.js тегом <script> в main_payment.tpl. */
function loadIframeResizer(): Promise<void> {
  if (typeof window.iFrameResize === 'function') return Promise.resolve()

  return new Promise<void>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${IFRAME_RESIZER_SRC}"]`)
    if (existing !== null) {
      existing.addEventListener('load', () => resolve())
      return
    }
    const script = document.createElement('script')
    script.src = IFRAME_RESIZER_SRC
    script.type = 'text/javascript'
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => resolve())
    document.head.appendChild(script)
  })
}
